import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/env.js';

const PatientInfo = ({ patientInfo, setPatientInfo, onValidationComplete, isExistingPatient, tenantId }) => {
  const { authFetch } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [errors, setErrors] = useState({});
  const [isValidated, setIsValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Store original patient info to detect changes
  const originalPatientInfoRef = useRef(null);

  // Initialize original patient info when component mounts or patient changes
  useEffect(() => {
    if (isExistingPatient && patientInfo.nom && patientInfo.prenom) {
      originalPatientInfoRef.current = { ...patientInfo };
    }
  }, [isExistingPatient]);

  const patientFields = [
    { key: 'nom', title: 'Last name', placeholder: 'E.g. Smith', type: 'text' },
    { key: 'prenom', title: 'First name', placeholder: 'E.g. John', type: 'text' },
    { key: 'sexe', title: 'Sex', placeholder: 'M/F', type: 'select', options: ['', 'M', 'F'] },
    { key: 'dateNaissance', title: 'Date of birth', placeholder: 'mm/dd/yyyy', type: 'date' },
    { key: 'adresse', title: 'Address', placeholder: 'E.g. 123 Main St, New York, NY 10001', type: 'text' }
  ];

  // Check if patient info has changed from original
  const hasChanges = () => {
    if (!isExistingPatient || !originalPatientInfoRef.current) return false;
    const original = originalPatientInfoRef.current;
    return patientFields.some(field =>
      (patientInfo[field.key] || '') !== (original[field.key] || '')
    );
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Generate preview string
  const generatePreviewString = () => {
    const parts = [];

    if (patientInfo.nom || patientInfo.prenom) {
      parts.push(`${patientInfo.prenom || ''} ${patientInfo.nom || ''}`.trim());
    }

    if (patientInfo.dateNaissance) {
      const age = calculateAge(patientInfo.dateNaissance);
      parts.push(`${formatDate(patientInfo.dateNaissance)} (${age} yrs)`);
    }

    if (patientInfo.sexe) {
      parts.push(patientInfo.sexe === 'M' ? 'Male' : 'Female');
    }

    if (patientInfo.adresse) {
      parts.push(patientInfo.adresse);
    }

    return parts.length > 0 ? parts.join(' • ') : 'No patient information entered';
  };

  // Check if form is valid
  const isFormValid = () => {
    return patientInfo.nom &&
           patientInfo.prenom &&
           patientInfo.sexe &&
           patientInfo.dateNaissance;
  };

  // Save patient updates to database
  const handleSavePatientChanges = async () => {
    if (!isFormValid() || !hasChanges()) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const requestBody = {
        // Original identifiers to find the patient
        original_first_name: originalPatientInfoRef.current.prenom,
        original_last_name: originalPatientInfoRef.current.nom,
        original_dob: originalPatientInfoRef.current.dateNaissance,
        // New values
        first_name: patientInfo.prenom,
        last_name: patientInfo.nom,
        dob: patientInfo.dateNaissance,
        sex: patientInfo.sexe,
        address: patientInfo.adresse
      };
      console.log('Updating patient with:', requestBody);

      const response = await authFetch(getApiUrl('/api/patients/update'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success) {
        setSaveSuccess(true);
        // Update original ref to reflect saved changes
        originalPatientInfoRef.current = { ...patientInfo };
        // Hide success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(data.message || 'Error while saving');
      }
    } catch (error) {
      console.error('Failed to save patient:', error);
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Validate and submit patient info
  const handleSubmit = () => {
    const newErrors = {};

    if (!patientInfo.nom?.trim()) newErrors.nom = 'Last name is required';
    if (!patientInfo.prenom?.trim()) newErrors.prenom = 'First name is required';
    if (!patientInfo.sexe) newErrors.sexe = 'Sex is required';
    if (!patientInfo.dateNaissance) newErrors.dateNaissance = 'Date of birth is required';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsExpanded(false);
      setIsValidated(true);
      if (onValidationComplete) {
        onValidationComplete(true);
      }
    }
  };

  const updatePatientInfo = (key, value) => {
    setPatientInfo(prev => ({
      ...prev,
      [key]: value
    }));

    // Clear error when field is filled
    if (errors[key] && value.trim()) {
      setErrors(prev => ({
        ...prev,
        [key]: ''
      }));
    }

    // Clear save success when editing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleToggle = () => {
    if (isExpanded) {
      // Try to submit when collapsing
      handleSubmit();
    } else {
      // Allow expanding to edit
      setIsExpanded(true);
      if (onValidationComplete) {
        onValidationComplete(false);
      }
    }
  };

  return (
    <div className="patient-info-panel" style={{ marginBottom: '20px' }}>
      <div style={{
        padding: '24px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header with toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          cursor: 'pointer'
        }}
        onClick={handleToggle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: '#ecfdf5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <span style={{ fontSize: '16px' }}>👤</span>
            </div>
            <h3 style={{
              color: '#111827',
              margin: 0,
              fontSize: '18px',
              fontWeight: '600'
            }}>
              Patient information
            </h3>
            {isExistingPatient && (
              <span style={{
                marginLeft: '12px',
                background: '#dbeafe',
                color: '#1d4ed8',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                Existing patient
              </span>
            )}
          </div>
          <div style={{
            background: '#f9fafb',
            color: '#6b7280',
            borderRadius: '20px',
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <span>{isExpanded ? 'Collapse' : 'Edit'}</span>
            <span style={{ fontSize: '10px' }}>{isExpanded ? '▲' : '▼'}</span>
          </div>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div style={{
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#059669', fontSize: '16px' }}>✓</span>
            <span style={{ color: '#065f46', fontSize: '14px', fontWeight: '500' }}>
              Patient information updated successfully
            </span>
          </div>
        )}

        {/* Error Message */}
        {saveError && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#dc2626', fontSize: '16px' }}>✕</span>
              <span style={{ color: '#991b1b', fontSize: '14px' }}>{saveError}</span>
            </div>
            <button
              onClick={() => setSaveError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Preview Mode */}
        {!isExpanded && (
          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            color: '#374151',
            lineHeight: '1.6'
          }}>
            {generatePreviewString()}
          </div>
        )}

        {/* Edit Mode */}
        {isExpanded && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {patientFields.map((field) => (
                <div key={field.key} style={{
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '12px',
                  position: 'relative'
                }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: errors[field.key] ? '#dc2626' : '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {field.title} {['nom', 'prenom', 'sexe', 'dateNaissance'].includes(field.key) && '*'}
                  </label>
                  {errors[field.key] && (
                    <div style={{
                      fontSize: '11px',
                      color: '#dc2626',
                      marginBottom: '4px'
                    }}>
                      {errors[field.key]}
                    </div>
                  )}

                  {field.type === 'select' ? (
                    <select
                      value={patientInfo[field.key] || ''}
                      onChange={(e) => updatePatientInfo(field.key, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${errors[field.key] ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        background: '#ffffff',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#059669';
                        e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={patientInfo[field.key] || ''}
                      onChange={(e) => updatePatientInfo(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${errors[field.key] ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        background: '#ffffff',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#059669';
                        e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '16px',
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleSubmit}
                style={{
                  background: isFormValid() ? '#059669' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isFormValid() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s'
                }}
                disabled={!isFormValid()}
              >
                ✓ Validate and continue
              </button>

              {/* Save Patient Changes Button - Only show for existing patients with changes */}
              {isExistingPatient && hasChanges() && (
                <button
                  onClick={handleSavePatientChanges}
                  disabled={isSaving || !isFormValid()}
                  style={{
                    background: isSaving ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSaving || !isFormValid() ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSaving ? (
                    <>
                      <span style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      Save patient changes
                    </>
                  )}
                </button>
              )}

              <div style={{
                padding: '12px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                flex: 1,
                minWidth: '200px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>ℹ️</span>
                  <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                    {isExistingPatient && hasChanges()
                      ? 'Unsaved changes'
                      : 'Required information to start the consultation'}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Keyframes for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PatientInfo;
